import { useEffect, useState } from "react";

import { assistantSessionsApi, type AssistantLinkedRoom } from "@/lib/api/client";

interface ThreadRooms {
  threadToken: string;
  rooms: Array<AssistantLinkedRoom>;
  readMark: number;
}

export interface ConversationLaunchRooms {
  /** Null until a list has loaded, a failed read included: a card must not offer Start while its room may already exist. */
  rooms: Array<AssistantLinkedRoom> | null;
  /** When the read behind `rooms` began, on the `takeRoomsReadMark` clock. 0 with no list. */
  readMark: number;
  /** The read and its retry both failed and there is no list to show. */
  hasReadFailed: boolean;
}

export const ROOMS_READ_RETRY_MS = 1500;

let lastRoomsReadMark = 0;

/**
 * The clock that orders room list reads against room creation, so a card can tell a list read
 * after its room was created from one that was only delivered later. Strictly increasing.
 */
export function takeRoomsReadMark(): number {
  lastRoomsReadMark += 1;
  return lastRoomsReadMark;
}

/**
 * The thread's rooms, for linking launch cards to the room each one started. Fetched only for a
 * thread that holds a card, again whenever `refreshKey` moves. A failed read is retried once.
 */
export function useConversationLaunchRooms(
  threadToken: string | undefined,
  isEnabled: boolean,
  refreshKey: string,
): ConversationLaunchRooms {
  const [loaded, setLoaded] = useState<ThreadRooms | null>(null);
  const [failedThreadToken, setFailedThreadToken] = useState<string | null>(null);

  useEffect(() => {
    if (!threadToken || !isEnabled) {
      return;
    }
    let isCancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const read = (canRetry: boolean) => {
      const readMark = takeRoomsReadMark();
      assistantSessionsApi.rooms(threadToken).then(
        (rooms) => {
          if (!isCancelled) {
            setLoaded({ threadToken, rooms, readMark });
            setFailedThreadToken(null);
          }
        },
        () => {
          /* Keep what was last read. A room started since then stays on its card until a list loads. */
          if (isCancelled) {
            return;
          }
          if (canRetry) {
            retryTimer = setTimeout(() => read(false), ROOMS_READ_RETRY_MS);
          } else {
            setFailedThreadToken(threadToken);
          }
        },
      );
    };
    read(true);
    return () => {
      isCancelled = true;
      clearTimeout(retryTimer);
    };
  }, [threadToken, isEnabled, refreshKey]);

  if (loaded && loaded.threadToken === threadToken) {
    return { rooms: loaded.rooms, readMark: loaded.readMark, hasReadFailed: false };
  }
  return { rooms: null, readMark: 0, hasReadFailed: failedThreadToken === threadToken };
}
